import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Grid,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  Tooltip,
  Checkbox,
  TextField,
} from '@mui/material';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiX,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { emailTemplatesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { showSuccess, showError } from '../utils/toast';
import { getSortIcon } from '../utils/sortIcon';
import TableLoader from '../components/TableLoader';
import { colors } from '../theme/modernTheme';
import { useMultiSelect } from '../hooks/useMultiSelect';
import { useDebounce } from '../hooks/useDebounce';
import BulkActionToolbar from '../components/BulkActionToolbar';
import BulkDeleteDialog from '../components/BulkDeleteDialog';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import { MODULE_PERMISSIONS } from '../utils/modulePermissions';
import { adjustPageAfterDeletion } from '../utils/paginationUtils';

export default function EmailTemplates() {
  const navigate = useNavigate();
  const { hasWritePermission } = useAuth();

  // RBAC: Check if user has write permission for email templates
  const canWrite = hasWritePermission(MODULE_PERMISSIONS.EMAIL_TEMPLATE);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Search state with debounce
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 500);

  // Sorting states
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Pagination states
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });

  // Multi-select state
  const multiSelect = useMultiSelect(templates, pagination.total, 'id');
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Custom toggleSelectAll that excludes templates with can_delete === false
  const handleToggleSelectAll = () => {
    // Filter out templates that cannot be deleted
    const selectableTemplates = templates.filter((t) => t.can_delete !== false);

    if (selectableTemplates.length === 0) return;

    const selectableIds = selectableTemplates.map((t) => t.id);
    const allSelectableSelected = selectableIds.every((id) => multiSelect.selectedIds.includes(id));

    if (allSelectableSelected && multiSelect.selectedIds.length === selectableIds.length) {
      // If all selectable items are selected and nothing else, clear selection
      multiSelect.clearSelection();
    } else {
      // Clear current selection and select only deletable templates
      multiSelect.clearSelection();
      selectableTemplates.forEach((template) => {
        multiSelect.toggleSelect(template);
      });
    }
  };

  // Permission checks - RBAC based
  const canCreate = canWrite;
  const canEdit = canWrite;
  const canDelete = canWrite;

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy: sortBy,
        sortOrder: sortOrder,
      };

      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      const response = await emailTemplatesAPI.getAll(params);
      setTemplates(response.data || []);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.total || 0,
        pages: response.pagination?.pages || 0,
      }));
    } catch (error) {
      setTemplates([]);
      const errorMessage = error.error?.message || error.message || 'Failed to load templates';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, pagination.page, pagination.limit, sortBy, sortOrder]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (pagination.page !== 1) {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleCreate = () => {
    navigate('/email-templates/new');
  };

  const handleEdit = (id) => {
    navigate(`/email-templates/${id}`);
  };

  const handleRowClick = (template) => {
    if (canEdit) {
      navigate(`/email-templates/${template.id}`);
    }
  };

  const handleDeleteClick = (template) => {
    if (template.can_delete === false) {
      showError('Cannot delete this template as it is currently being used in active campaigns.');
      return;
    }
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await emailTemplatesAPI.delete(templateToDelete.id);
      multiSelect.removeFromSelection(templateToDelete.id);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      showSuccess(`Template "${templateToDelete.name}" deleted successfully`);

      // Adjust page if needed after deletion
      const newTotalCount = pagination.total - 1;
      adjustPageAfterDeletion(pagination.page - 1, newTotalCount, pagination.limit, (newPage) => {
        setPagination((prev) => ({ ...prev, page: newPage + 1 }));
      });

      loadTemplates();
    } catch (error) {
      const errorMessage = error.error?.message || error.message || 'Failed to delete template';
      showError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleBulkDelete = async () => {
    // Check if any selected templates cannot be deleted
    const selectedTemplates = templates.filter((t) => multiSelect.selectedIds.includes(t.id));
    const nonDeletableTemplates = selectedTemplates.filter((t) => t.can_delete === false);

    if (nonDeletableTemplates.length > 0) {
      showError(
        `Cannot delete ${nonDeletableTemplates.length} template(s) as they are currently being used in active campaigns. Please deselect them first.`,
      );
      return;
    }

    setBulkDeleting(true);
    try {
      const selectionData = multiSelect.getSelectionData();
      const deleteCount = multiSelect.selectedCount;

      const filters = {};
      if (debouncedSearch) filters.search = debouncedSearch;

      await emailTemplatesAPI.bulkDelete({
        ...selectionData,
        filters,
      });

      setBulkDeleteDialogOpen(false);
      multiSelect.clearSelection();

      // Adjust page if needed after bulk deletion
      const newTotalCount = pagination.total - deleteCount;
      adjustPageAfterDeletion(pagination.page - 1, newTotalCount, pagination.limit, (newPage) => {
        setPagination((prev) => ({ ...prev, page: newPage + 1 }));
      });

      loadTemplates();
      showSuccess(`Successfully deleted ${deleteCount} template(s)`);
    } catch (error) {
      showError(error?.error?.message || 'Failed to delete templates');
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <Box>
      {/* Header Section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={1}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: colors.text.primary,
          }}
        >
          Email Templates
        </Typography>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<FiPlus />}
            onClick={handleCreate}
            sx={{
              backgroundColor: colors.primary.main,
              '&:hover': { backgroundColor: colors.primary.dark },
              borderRadius: '4px',
            }}
          >
            Add Template
          </Button>
        )}
      </Box>

      {/* Search and Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '4px',
          boxShadow: 'none',
          opacity: multiSelect.selectedCount > 0 ? 0.5 : 1,
          pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search templates..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              disabled={multiSelect.selectedCount > 0}
              InputProps={{
                endAdornment: searchInput ? (
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput('')}
                    disabled={multiSelect.selectedCount > 0}
                    sx={{ padding: '4px' }}
                  >
                    <FiX size={16} style={{ color: colors.text.secondary }} />
                  </IconButton>
                ) : (
                  <FiSearch style={{ marginRight: 8, color: colors.text.secondary }} />
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.background.paper,
                  borderRadius: '4px',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
              }}
            >
              <IconButton
                size="small"
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    page: Math.max(1, prev.page - 1),
                  }))
                }
                disabled={pagination.page === 1 || multiSelect.selectedCount > 0}
                sx={{
                  borderRadius: 1,
                  marginRight: -2,
                }}
              >
                <FiChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  minWidth: '100px',
                  textAlign: 'center',
                }}
              >
                Page {pagination.page} of {pagination.pages || 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    page: Math.min(pagination.pages, prev.page + 1),
                  }))
                }
                disabled={pagination.page >= pagination.pages || multiSelect.selectedCount > 0}
                sx={{
                  borderRadius: 1,
                  marginLeft: -2,
                }}
              >
                <FiChevronRight />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedCount={multiSelect.selectedCount}
        onClearSelection={multiSelect.clearSelection}
        onDelete={() => setBulkDeleteDialogOpen(true)}
        canDelete={canDelete}
        itemName="templates"
      />

      {/* Templates Table */}
      {loading ? (
        <TableLoader message="Loading email templates..." />
      ) : templates.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: colors.text.secondary,
          }}
        >
          <Typography variant="body1">No email templates found</Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 'none',
            border: `1px solid ${colors.border.main}`,
            borderRadius: '4px',
            overflowX: 'auto',
          }}
        >
          <Table>
            <TableHead sx={{ borderBottom: '1px solid #E5E7EB' }}>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: '2%', py: 1, px: 1 }}>
                  <Checkbox
                    size="medium"
                    indeterminate={multiSelect.isSomeCurrentPageSelected}
                    checked={multiSelect.isAllCurrentPageSelected}
                    onChange={handleToggleSelectAll}
                    sx={{
                      padding: '4px',
                      color: colors.text.secondary,
                      '& .MuiSvgIcon-root': { fontSize: 28 },
                      transform: 'scale(1.2)',
                      '&.Mui-checked': {
                        color: colors.primary.main,
                      },
                      '&.MuiCheckbox-indeterminate': {
                        color: colors.primary.main,
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    paddingLeft: 0,
                    width: '200px',
                    fontWeight: 600,
                    color:
                      multiSelect.selectedCount > 0 ? colors.text.disabled : colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: multiSelect.selectedCount > 0 ? 'not-allowed' : 'pointer',
                    userSelect: 'none',
                    pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
                  }}
                  onClick={() => handleSort('name')}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Template Name
                    {getSortIcon(sortBy, sortOrder, 'name')}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: '250px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Subject
                </TableCell>
                <TableCell
                  sx={{
                    width: '200px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Internal Notes
                </TableCell>
                <TableCell
                  sx={{
                    width: '140px',
                    fontWeight: 600,
                    color:
                      multiSelect.selectedCount > 0 ? colors.text.disabled : colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    cursor: multiSelect.selectedCount > 0 ? 'not-allowed' : 'pointer',
                    userSelect: 'none',
                    pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
                  }}
                  onClick={() => handleSort('updated_at')}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Last Updated
                    {getSortIcon(sortBy, sortOrder, 'updated_at')}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: '80px',
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {multiSelect.selectedCount > 0 ? '' : 'Actions'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow
                  key={template.id}
                  onClick={() => handleRowClick(template)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: multiSelect.isSelected(template)
                      ? colors.primary.main + '10'
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: multiSelect.isSelected(template)
                        ? colors.primary.main + '20'
                        : colors.action.row,
                    },
                    '&:not(:last-of-type)': {
                      borderBottom: `1px solid ${colors.border.main}`,
                    },
                  }}
                >
                  <TableCell
                    padding="checkbox"
                    sx={{ py: 1.5, px: 1, width: '20px' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip
                      title={
                        template.can_delete === false
                          ? 'Cannot delete: template is used in active campaigns'
                          : ''
                      }
                      arrow
                      placement="top"
                    >
                      <span>
                        <Checkbox
                          size="medium"
                          disabled={template.can_delete === false}
                          checked={multiSelect.isSelected(template)}
                          onChange={(e) => {
                            e.stopPropagation();
                            multiSelect.toggleSelect(template);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          sx={{
                            padding: '4px',
                            '& .MuiSvgIcon-root': { fontSize: 28 },
                            transform: 'scale(1.2)',
                            color:
                              template.can_delete === false
                                ? colors.text.disabled
                                : colors.text.secondary,
                            '&.Mui-checked': {
                              color: colors.primary.main,
                            },
                            '&.Mui-disabled': {
                              color: colors.text.disabled,
                              cursor: 'not-allowed',
                            },
                          }}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    sx={{
                      paddingLeft: 0,
                      maxWidth: '200px',
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                      py: 1,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.875rem',
                        }}
                      >
                        <Tooltip title={template.name} arrow placement="top">
                          <span>{template.name}</span>
                        </Tooltip>
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: '250px',
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                      py: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.875rem',
                      }}
                    >
                      <Tooltip title={template.subject || 'No subject'} arrow placement="top">
                        <span>{template.subject || 'No subject'}</span>
                      </Tooltip>
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1, maxWidth: '200px' }}>
                    {template.notes ? (
                      <Tooltip title={template.notes} arrow placement="top">
                        <Typography
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.875rem',
                            color: colors.text.primary,
                          }}
                        >
                          {template.notes}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" sx={{ color: colors.text.disabled }}>
                        No notes
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '140px',
                      fontSize: '0.875rem',
                      color: colors.text.primary,
                      py: 1,
                    }}
                  >
                    {template.updated_at
                      ? new Date(template.updated_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : '--'}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} sx={{ py: 1 }}>
                    {multiSelect.selectedCount > 0 ? (
                      <Box sx={{ width: '72px' }} />
                    ) : (
                      <Box sx={{ display: 'flex', gap: '4px', minWidth: '72px' }}>
                        {canEdit ? (
                          <IconButton
                            size="small"
                            sx={{
                              color: colors.text.primary,
                              p: 1,
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: colors.primary.main,
                              },
                            }}
                            onClick={() => handleEdit(template.id)}
                            title="Edit"
                          >
                            <FiEdit2 size={16} />
                          </IconButton>
                        ) : (
                          <Box sx={{ width: '36px' }} />
                        )}

                        {canDelete && (
                          <Tooltip
                            title={
                              template.can_delete === false
                                ? 'Cannot delete: template is used in active campaigns'
                                : 'Delete'
                            }
                            arrow
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={template.can_delete === false}
                                sx={{
                                  color:
                                    template.can_delete === false
                                      ? colors.text.disabled
                                      : colors.text.primary,
                                  p: 1,
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                    color:
                                      template.can_delete === false
                                        ? colors.text.disabled
                                        : colors.error.main,
                                  },
                                  '&.Mui-disabled': {
                                    color: colors.text.disabled,
                                    cursor: 'not-allowed',
                                  },
                                }}
                                onClick={() => handleDeleteClick(template)}
                              >
                                <FiTrash2 size={16} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Bottom Pagination */}
      {!loading && templates.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: 2,
            gap: 1,
            opacity: multiSelect.selectedCount > 0 ? 0.5 : 1,
            pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
          }}
        >
          <IconButton
            size="small"
            onClick={() =>
              setPagination((prev) => ({
                ...prev,
                page: Math.max(1, prev.page - 1),
              }))
            }
            disabled={pagination.page === 1 || multiSelect.selectedCount > 0}
            sx={{
              marginRight: -2,
              borderRadius: 1,

              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
              },
            }}
          >
            <FiChevronLeft />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              minWidth: '100px',
              textAlign: 'center',
            }}
          >
            Page {pagination.page} of {pagination.pages || 1}
          </Typography>
          <IconButton
            size="small"
            onClick={() =>
              setPagination((prev) => ({
                ...prev,
                page: Math.min(pagination.pages, prev.page + 1),
              }))
            }
            disabled={pagination.page >= pagination.pages || multiSelect.selectedCount > 0}
            sx={{
              marginLeft: -2,
              borderRadius: 1,

              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
              },
            }}
          >
            <FiChevronRight />
          </IconButton>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName="template"
        deleting={deleting}
      />

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        count={multiSelect.selectedCount}
        itemName="templates"
        deleting={bulkDeleting}
      />
    </Box>
  );
}
